import {Icon} from "@iconify/react";
import {Link} from "@tanstack/react-router";
import {useEffect, useRef, useState} from "react";
import ArrowUpRightIcon from "../icons/ArrowUpRightIcon.tsx";

const STEPS = [
    {
        icon:        'material-symbols-light:trophy-outline',
        title:       'Create',
        description: 'Set up a tournament or league in minutes — pick your sport and format.',
    },
    {
        icon:        'material-symbols-light:how-to-reg-outline',
        title:       'Register',
        description: 'Players and teams sign up through your public registration form.',
    },
    {
        icon:        'material-symbols-light:payments-outline',
        title:       'Take payment',
        description: 'Entry fees collected automatically at sign-up via secure checkout.',
    },
    {
        icon:        'material-symbols-light:account-tree-outline',
        title:       'Draw & share',
        description: 'Generate the draw or schedule and share a live public link.',
    },
    {
        icon:        'material-symbols-light:scoreboard-outline',
        title:       'Record results',
        description: 'Record scores live — brackets and standings update instantly.',
    },
];

const HowItWorksSection = () => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {threshold: 0.2},
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="how-it-works" className="scroll-mt-24 py-16 lg:py-24 bg-[#EDEEF4] overflow-hidden" ref={ref}>
            <div className="container mx-auto px-6 md:px-12">
                <div
                    className={`text-center max-w-3xl mx-auto mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    <span className="inline-flex items-center gap-2 font-inter text-[#082F8A] uppercase tracking-[0.2em] text-sm font-semibold bg-[#082F8A]/10 rounded-full px-4 py-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#082F8A] animate-pulse" /> How it works
                    </span>
                    <h2 className="font-bebas text-[clamp(36px,6vw,60px)] leading-none mt-4 mb-4 text-[#1F2022] uppercase">
                        From sign-up to results in <span className="text-[#082F8A]">five steps</span>
                    </h2>
                    <p className="font-inter text-[#1F2022]/80 text-lg max-md:text-base">
                        Set it up, open registration, and let ScorePal run the rest — payments, draws and live scoring included.
                    </p>
                </div>

                <div className="relative">
                    {/* connecting flow line (desktop) */}
                    <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 z-0">
                        <div
                            className="h-full bg-gradient-to-r from-[#082F8A]/0 via-[#082F8A]/40 to-[#082F8A]/0 origin-left transition-transform duration-1000 ease-out"
                            style={{transform: visible ? 'scaleX(1)' : 'scaleX(0)'}}
                        />
                    </div>

                    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                        {STEPS.map((step, i) => (
                            <div
                                key={i}
                                className={`group relative bg-white rounded-3xl p-6 shadow-sm border border-transparent
                                    transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#082F8A]/10 hover:border-[#082F8A]/20
                                    ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{transitionDelay: visible ? `${i * 120}ms` : '0ms'}}
                            >
                                <span className="absolute top-4 right-5 font-bebas text-5xl leading-none text-[#082F8A]/10 group-hover:text-[#082F8A]/20 transition-colors">
                                    {i + 1}
                                </span>
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#082F8A] to-[#3b62c4] shadow-lg shadow-[#082F8A]/25 flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3">
                                    <Icon icon={step.icon} className="text-3xl text-white" />
                                </div>
                                <h3 className="font-bebas text-2xl text-[#082F8A] leading-none mb-2 tracking-wide">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/75 text-sm leading-relaxed">{step.description}</p>

                                {/* arrow to next step (desktop) */}
                                {i < STEPS.length - 1 && (
                                    <div className="hidden lg:flex absolute top-11 -right-4 z-20 w-7 h-7 rounded-full bg-white shadow-md items-center justify-center text-[#082F8A] opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Icon icon="material-symbols-light:arrow-forward" className="text-lg" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    className={`flex justify-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    <Link
                        to={"/signup/" as any}
                        className="flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-[#06246b] transition group"
                    >
                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                        <div className="bg-white rounded-full p-1 transition-transform group-hover:translate-x-0.5">
                            <ArrowUpRightIcon className="text-[#082F8A]" size={28} />
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;
